import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../utils/customBaseQuery";

export interface ForumPost {
  id: string;
  contentId: string;
  contentType: string;
  userId: string;
  org: string;
  body: string;
  createdAt: string;
  parentId: string | null;
  upvotes: string[];
  pinned: boolean;
}

const postsTagId = (contentId: string, contentType: string) =>
  `${contentType}:${contentId}`;

// Helper to patch cache for both sort variants (optimistic updates don't know current sort)
const patchBothSorts = (
  dispatch: any,
  contentId: string,
  contentType: string,
  updater: (draft: any, sort: "recent" | "top") => void
) => {
  for (const sort of ["recent", "top"] as const) {
    dispatch(
      DiscussionApi.util.updateQueryData(
        "getPosts",
        { contentId, contentType, cursor: null, sort },
        (draft) => updater(draft, sort)
      )
    );
  }
};

const topLevelSort =
  (sort: "recent" | "top") => (a: ForumPost, b: ForumPost) => {
    // Pinned posts always first (moderator intent), ordered by pin date.
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (sort === "top") {
      const upvoteDiff = b.upvotes.length - a.upvotes.length;
      if (upvoteDiff !== 0) return upvoteDiff;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

const sortPostsForDisplay = (posts: ForumPost[], sort: "recent" | "top") => {
  const topLevel = posts
    .filter((p) => p.parentId === null)
    .sort(topLevelSort(sort));
  const repliesByParent = new Map<string, ForumPost[]>();

  for (const post of posts) {
    if (post.parentId === null) continue;
    const replies = repliesByParent.get(post.parentId) ?? [];
    replies.push(post);
    repliesByParent.set(post.parentId, replies);
  }

  return topLevel.flatMap((post) => [
    post,
    ...(repliesByParent.get(post.id) ?? []).sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  ]);
};

export const DiscussionApi = createApi({
  reducerPath: "DiscussionApi",
  baseQuery: customBaseQuery,
  tagTypes: ["Posts"],
  endpoints: (builder) => ({
    getPosts: builder.query<
      {
        posts: ForumPost[];
        total: number;
        totalRepliesByPost: Record<string, number>;
        nextCursor: string | null;
      },
      {
        contentId: string;
        contentType: string;
        cursor: string | null;
        sort: "recent" | "top";
      }
    >({
      query: ({ contentId, contentType, cursor, sort }) => ({
        url: `/discussions/posts?contentId=${encodeURIComponent(contentId)}&contentType=${encodeURIComponent(contentType)}&cursor=${cursor ?? ""}&sort=${sort}`,
        method: "GET"
      }),
      extraOptions: { service: "Discussions" },
      // Pages for the same content+sort share one cache entry; different sorts get separate entries.
      serializeQueryArgs: ({ queryArgs }) =>
        `${postsTagId(queryArgs.contentId, queryArgs.contentType)}:${queryArgs.sort}`,
      merge: (cache, incoming, { arg }) => {
        if (arg.cursor === null) {
          cache.posts = incoming.posts;
          cache.total = incoming.total;
          cache.totalRepliesByPost = incoming.totalRepliesByPost;
        } else {
          const existingIds = new Set(cache.posts.map((p) => p.id));
          cache.posts.push(
            ...incoming.posts.filter((p) => !existingIds.has(p.id))
          );
          cache.total = incoming.total;
          cache.totalRepliesByPost = {
            ...cache.totalRepliesByPost,
            ...incoming.totalRepliesByPost
          };
        }
        cache.nextCursor = incoming.nextCursor;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.cursor !== previousArg?.cursor ||
        currentArg?.sort !== previousArg?.sort,
      providesTags: (_result, _error, { contentId, contentType }) => [
        { type: "Posts", id: postsTagId(contentId, contentType) }
      ]
    }),

    addPost: builder.mutation<
      ForumPost,
      {
        contentId: string;
        contentType: string;
        body: string;
        parentId: string | null;
      }
    >({
      query: ({ contentId, contentType, body, parentId }) => ({
        url: "/discussions/posts",
        method: "POST",
        body: { contentId, contentType, body, parentId }
      }),
      extraOptions: { service: "Discussions" },
      onQueryStarted: async (
        { contentId, contentType, parentId },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: createdPost } = await queryFulfilled;

          if (!parentId) {
            // Top-level post: prepend to cache and increment total.
            patchBothSorts(dispatch, contentId, contentType, (draft, sort) => {
              const alreadyExists = draft.posts.some(
                (p: ForumPost) => p.id === createdPost.id
              );
              if (!alreadyExists) {
                draft.posts.unshift(createdPost);
                draft.posts = sortPostsForDisplay(draft.posts, sort);
                draft.total = (draft.total ?? 0) + 1;
              }
            });
          } else {
            // Reply: append under its parent.
            patchBothSorts(dispatch, contentId, contentType, (draft, sort) => {
              const parentExists = draft.posts.some(
                (p: ForumPost) => p.id === parentId
              );
              const alreadyExists = draft.posts.some(
                (p: ForumPost) => p.id === createdPost.id
              );
              if (parentExists && !alreadyExists) {
                draft.posts.push(createdPost);
                draft.posts = sortPostsForDisplay(draft.posts, sort);
                draft.totalRepliesByPost[parentId] =
                  (draft.totalRepliesByPost[parentId] ?? 0) + 1;
              }
            });
          }
        } catch {
          // The mutation error is surfaced by the caller component.
        }
      }
    }),

    deletePost: builder.mutation<
      { id: string },
      { postId: string; contentId: string; contentType: string }
    >({
      query: ({ postId }) => ({
        url: `/discussions/posts/${postId}`,
        method: "DELETE"
      }),
      extraOptions: { service: "Discussions" },
      invalidatesTags: (_result, _error, { contentId, contentType }) => [
        { type: "Posts", id: postsTagId(contentId, contentType) }
      ],
      onQueryStarted: async (
        { postId, contentId, contentType },
        { dispatch, queryFulfilled }
      ) => {
        // Optimistically remove the post and all its replies from the cache.
        const patches = (["recent", "top"] as const).map((sort) =>
          dispatch(
            DiscussionApi.util.updateQueryData(
              "getPosts",
              { contentId, contentType, cursor: null, sort },
              (draft) => {
                const target = draft.posts.find(
                  (p: ForumPost) => p.id === postId
                );
                const isTopLevel = target?.parentId === null;
                const parentId = target?.parentId ?? null;
                // Remove the post and any of its children
                draft.posts = draft.posts.filter(
                  (p: ForumPost) => p.id !== postId && p.parentId !== postId
                );
                if (isTopLevel) {
                  draft.total = Math.max(0, draft.total - 1);
                } else if (
                  parentId &&
                  draft.totalRepliesByPost[parentId] != null
                ) {
                  draft.totalRepliesByPost[parentId] = Math.max(
                    0,
                    draft.totalRepliesByPost[parentId] - 1
                  );
                }
              }
            )
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      }
    }),

    reportPost: builder.mutation<void, { postId: string }>({
      query: ({ postId }) => ({
        url: `/discussions/posts/${postId}/report`,
        method: "POST"
      }),
      extraOptions: { service: "Discussions" }
      // No cache invalidation — reporting is silent, post stays visible.
    }),

    editPost: builder.mutation<
      ForumPost,
      { postId: string; contentId: string; contentType: string; body: string }
    >({
      query: ({ postId, body }) => ({
        url: `/discussions/posts/${postId}`,
        method: "PATCH",
        body: { body }
      }),
      extraOptions: { service: "Discussions" },
      invalidatesTags: (_result, _error, { contentId, contentType }) => [
        { type: "Posts", id: postsTagId(contentId, contentType) }
      ],
      onQueryStarted: async (
        { postId, contentId, contentType, body },
        { dispatch, queryFulfilled }
      ) => {
        // Optimistically update the body in cache.
        const patches = (["recent", "top"] as const).map((sort) =>
          dispatch(
            DiscussionApi.util.updateQueryData(
              "getPosts",
              { contentId, contentType, cursor: null, sort },
              (draft) => {
                const post = draft.posts.find(
                  (p: ForumPost) => p.id === postId
                );
                if (post) post.body = body;
              }
            )
          )
        );
        try {
          const { data: updatedPost } = await queryFulfilled;
          patchBothSorts(dispatch, contentId, contentType, (draft) => {
            const idx = draft.posts.findIndex(
              (p: ForumPost) => p.id === postId
            );
            if (idx >= 0) draft.posts[idx] = updatedPost;
          });
        } catch {
          patches.forEach((p) => p.undo());
        }
      }
    }),

    getReplies: builder.query<
      { replies: ForumPost[]; total: number; nextCursor: string | null },
      { postId: string; cursor: string | null; sort: "recent" | "top" }
    >({
      query: ({ postId, cursor, sort }) => ({
        url: `/discussions/posts/${postId}/replies?cursor=${cursor ?? ""}&sort=${sort}`,
        method: "GET"
      }),
      extraOptions: { service: "Discussions" }
    }),

    toggleUpvote: builder.mutation<
      ForumPost,
      { postId: string; contentId: string; contentType: string; userId: string }
    >({
      query: ({ postId }) => ({
        url: `/discussions/posts/${postId}/upvote`,
        method: "PATCH"
      }),
      extraOptions: { service: "Discussions" },
      invalidatesTags: (_result, _error, { contentId, contentType }) => [
        { type: "Posts", id: postsTagId(contentId, contentType) }
      ],
      onQueryStarted: async (
        { postId, contentId, contentType, userId },
        { dispatch, queryFulfilled }
      ) => {
        const patches = (["recent", "top"] as const).map((sort) =>
          dispatch(
            DiscussionApi.util.updateQueryData(
              "getPosts",
              { contentId, contentType, cursor: null, sort },
              (draft) => {
                const post = draft.posts.find(
                  (p: ForumPost) => p.id === postId
                );
                if (!post) return;
                const idx = post.upvotes.indexOf(userId);
                if (idx >= 0) post.upvotes.splice(idx, 1);
                else post.upvotes.push(userId);
                draft.posts = sortPostsForDisplay(draft.posts, sort);
              }
            )
          )
        );
        try {
          const { data: updatedPost } = await queryFulfilled;
          patchBothSorts(dispatch, contentId, contentType, (draft, sort) => {
            const idx = draft.posts.findIndex(
              (p: ForumPost) => p.id === postId
            );
            if (idx < 0) return;
            draft.posts[idx] = updatedPost;
            draft.posts = sortPostsForDisplay(draft.posts, sort);
          });
        } catch {
          patches.forEach((p) => p.undo());
        }
      }
    }),

    pinPost: builder.mutation<
      ForumPost,
      { postId: string; contentId: string; contentType: string }
    >({
      query: ({ postId }) => ({
        url: `/discussions/posts/${postId}/pin`,
        method: "PATCH"
      }),
      extraOptions: { service: "Discussions" },
      invalidatesTags: (_result, _error, { contentId, contentType }) => [
        { type: "Posts", id: postsTagId(contentId, contentType) }
      ]
    })
  })
});

export const {
  useGetPostsQuery,
  useGetRepliesQuery,
  useLazyGetRepliesQuery,
  useAddPostMutation,
  useEditPostMutation,
  useDeletePostMutation,
  useToggleUpvoteMutation,
  useReportPostMutation,
  usePinPostMutation
} = DiscussionApi;
